import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calculator, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const FIELDS = [
  ['loan_amount', 'Loan amount ($)'], ['purchase_price', 'Purchase price ($)'], ['property_value', 'Property value ($)'],
  ['arv', 'After-repair value ($)'], ['rehab_budget', 'Rehab / construction budget ($)'], ['fico', 'Exact FICO'],
  ['investor_experience', 'Completed deals (36 mo)'], ['monthly_rent', 'Monthly rent ($)'], ['monthly_debt_service', 'Monthly debt service ($)'],
];
const STATUS_CLS = { calculated: 'bg-amber-100 text-amber-800', needs_lender_confirmation: 'bg-zinc-100 text-zinc-700', ineligible: 'bg-red-100 text-red-800' };
const STATUS_LABEL = { calculated: 'Calculated (preliminary)', needs_lender_confirmation: 'Needs lender confirmation', ineligible: 'Ineligible' };

// Borrower scenario inputs + preliminary pricing from approved lender rate sheets.
export const LeadPricingPanel = ({ lead }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/leads/${lead.lead_id}/price`).then((r) => {
      const sc = r.data.scenario || {};
      setForm(Object.fromEntries(FIELDS.map(([k]) => [k, sc[k] ?? ''])));
      setPricing(r.data.pricing);
    }).catch(() => {});
  }, [lead.lead_id]);

  const save = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)]));
    try {
      const r = await api.put(`/leads/${lead.lead_id}/scenario`, body);
      const m = r.data.scenario;
      toast.success(`Scenario saved · LTV ${m.ltv ?? '?'}% · LTC ${m.ltc ?? '?'}% · ARV-LTV ${m.arv_ltv ?? '?'}%`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const price = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.lead_id}/price`);
      setPricing(r.data);
      toast.success(r.data.approved_rule_sets ? `Priced against ${r.data.approved_rule_sets} approved rate sheet(s)` : 'No approved rate sheets yet — upload and approve lender pricing first');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pricing failed');
    } finally {
      setBusy(false);
    }
  };

  const termSheet = async (q) => {
    try {
      const r = await api.post('/term-sheets', {
        lead_id: lead.lead_id, lender_id: q.lender_id, program_id: null, quote: q,
        loan_amount: pricing.scenario.loan_amount, property_address: lead.property_address || 'Not provided',
      });
      toast.success('Preliminary term sheet drafted from this quote — review and approve before sending');
      navigate(`/term-sheets?open=${r.data.term_sheet_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create term sheet');
    }
  };

  return (
    <div className="space-y-4" data-testid="lead-pricing-panel">
      <div className="grid sm:grid-cols-3 gap-2">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-xs text-muted-foreground">{label}</label>
            <Input type="number" value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8" data-testid={`scenario-${k}`} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={save} data-testid="scenario-save-btn"><Save className="h-4 w-4 mr-1" /> Save scenario</Button>
        <Button size="sm" onClick={price} disabled={busy} data-testid="price-lead-btn"><Calculator className="h-4 w-4 mr-1" /> {busy ? 'Pricing...' : 'Price with approved rate sheets'}</Button>
      </div>
      {pricing && (
        <div className="space-y-2" data-testid="pricing-quotes">
          <p className="text-xs text-muted-foreground">
            Priced {new Date(pricing.priced_at).toLocaleString()} · LTV {pricing.scenario?.ltv ?? '?'}% · LTC {pricing.scenario?.ltc ?? '?'}% · ARV-LTV {pricing.scenario?.arv_ltv ?? '?'}% · broker {pricing.broker_comp?.origination_points ?? 0} pt(s)
          </p>
          {pricing.quotes.length === 0 && <p className="text-sm text-muted-foreground">No approved rate sheets to price against.</p>}
          {pricing.quotes.map((q, i) => (
            <div key={`${q.rule_set_id}-${q.program_name}-${i}`} className="border rounded-lg p-3" data-testid={`quote-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{q.lender_name} <span className="text-muted-foreground font-normal">· {q.program_name}</span></p>
                  <p className="text-xs text-muted-foreground">Rate sheet v{q.rule_set_version}{q.effective_date ? ` · effective ${q.effective_date}` : ''}</p>
                </div>
                <Badge className={STATUS_CLS[q.status]}>{STATUS_LABEL[q.status]}</Badge>
              </div>
              {q.status === 'calculated' && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm mt-2">
                  <div><p className="text-xs text-muted-foreground">Rate</p><p className="font-semibold">{q.interest_rate}%{q.rate_adjustment ? <span className="text-xs text-muted-foreground"> (base {q.base_rate} {q.rate_adjustment > 0 ? '+' : ''}{q.rate_adjustment})</span> : null}</p></div>
                  <div><p className="text-xs text-muted-foreground">Points</p><p className="font-semibold">{q.total_points} <span className="text-xs text-muted-foreground">({q.lender_points} + {q.broker_points} broker)</span></p></div>
                  <div><p className="text-xs text-muted-foreground">Fees</p><p className="font-semibold">${(q.lender_fee_total + (q.broker_processing_fee || 0)).toLocaleString()}</p></div>
                  <div><p className="text-xs text-muted-foreground">Term</p><p className="font-semibold">{q.term_months ?? '—'} mo {q.interest_only ? 'IO' : ''}</p></div>
                  <div><p className="text-xs text-muted-foreground">Est. payment</p><p className="font-semibold">${q.estimated_payment?.monthly_payment?.toLocaleString()}</p></div>
                </div>
              )}
              {q.blockers?.length > 0 && <ul className="text-xs text-red-700 mt-2 list-disc list-inside">{q.blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
              {q.conditions?.length > 0 && <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">{q.conditions.map((c) => <li key={c}>{c}</li>)}</ul>}
              {q.adjustments_applied?.length > 0 && <p className="text-xs text-muted-foreground mt-1">Adjustments: {q.adjustments_applied.join(', ')}</p>}
              <p className="text-[11px] text-muted-foreground mt-1 italic">{q.label}</p>
              {q.status === 'calculated' && (
                <Button size="sm" variant="outline" className="mt-2" onClick={() => termSheet(q)} data-testid={`quote-${i}-termsheet-btn`}><FileText className="h-3.5 w-3.5 mr-1" /> Preliminary term sheet</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadPricingPanel;
